/*
 * Signup Router — role-aware entry point for MySchoolRx.
 * Three paths: School User (single school), Portfolio / Authorizer User, and
 * 589 Labs (Super Admin, invite-only). Prevents self-assignment of admin rights.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Building2, Network, ArrowRight, Lock, Loader2 } from "lucide-react";

const ROUTE = "/myschoolrx";
const API = process.env.REACT_APP_BACKEND_URL;

const CARDS = [
  {
    key: "school",
    icon: Building2,
    title: "I'm setting this up for one school",
    subtitle: "School User",
    desc: "Set up one school's workspace, complete your diagnostic, and generate a board-ready report.",
    cta: "Start school signup",
    action: (navigate) => navigate(`${ROUTE}/signup`),
    accent: "#007a9e",
    testid: "signup-router-school",
  },
  {
    key: "portfolio",
    icon: Network,
    title: "I manage or oversee multiple schools",
    subtitle: "Portfolio / Authorizer User",
    desc: "Authorizers, EMOs, CMOs, districts, networks, and portfolio managers onboard a group of schools with bracket pricing and a single portfolio license.",
    cta: "Create your Portfolio account",
    action: (navigate) => navigate("/signup/portfolio"),
    accent: "#0ea5e9",
    testid: "signup-router-portfolio",
  },
];

const RoleCard = ({ card, navigate }) => {
  const Icon = card.icon;
  return (
    <button
      type="button"
      onClick={() => card.action(navigate)}
      className="group text-left rounded-2xl border border-[#e5e7eb] bg-white p-6 hover:border-[#007a9e]/50 hover:shadow-lg transition-all flex flex-col"
      data-testid={card.testid}
    >
      <div
        className="inline-flex h-12 w-12 items-center justify-center rounded-xl shrink-0"
        style={{ background: `${card.accent}14`, color: card.accent }}
      >
        <Icon className="h-6 w-6" />
      </div>
      <div className="mt-4 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6b7280]">{card.subtitle}</div>
      <h2 className="mt-1 text-lg font-bold text-[#111827] leading-tight">{card.title}</h2>
      <p className="mt-2 text-[13px] text-[#4b5563] leading-relaxed flex-1">{card.desc}</p>
      {card.note && (
        <div className="mt-3 flex items-start gap-1.5 text-[11px] text-[#92400e] bg-[#fffbeb] border border-[#fde68a] rounded-md px-2.5 py-1.5">
          <Lock className="h-3 w-3 mt-0.5 shrink-0" /> <span>{card.note}</span>
        </div>
      )}
      <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: card.accent }}>
        {card.cta} <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </button>
  );
};

const SignupRouter = () => {
  const navigate = useNavigate();
  // Founding Partner safety net: when the admin has the landing CTA in Founding
  // Partner mode, "Get Started" must go straight to no-payment FP onboarding,
  // never the commercial signup router.
  const [checking, setChecking] = React.useState(true);
  React.useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/landing-cta`)
      .then((r) => {
        if (!alive) return;
        if (r.data?.mode === "founding_partner") navigate(`${ROUTE}/pilot`, { replace: true });
        else setChecking(false);
      })
      .catch(() => alive && setChecking(false));
    return () => { alive = false; };
  }, [navigate]);
  if (checking) {
    return <div className="min-h-screen grid place-items-center bg-[#f9fafb]" data-testid="signup-router-loading"><Loader2 className="h-6 w-6 animate-spin text-[#94a3b8]" /></div>;
  }
  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="signup-router-page">
      <header className="sticky top-0 z-30 bg-white border-b border-[#e5e7eb]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 flex items-center justify-between h-20">
          <button onClick={() => navigate(ROUTE)} className="flex items-center shrink-0" data-testid="signup-router-logo">
            <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 w-auto" />
          </button>
          <a href="/diagnostic/login" className="text-[13px] font-semibold text-[#374151] hover:text-[#111827]" data-testid="signup-router-signin">
            Sign in
          </a>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#007a9e] font-semibold">Get started</span>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#111827] leading-tight">How would you like to get started?</h1>
          <p className="mt-3 text-[15px] text-[#4b5563]">Choose the option that best describes you. We'll route you to the right onboarding flow.</p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto" data-testid="signup-router-cards">
          {CARDS.map((c) => <RoleCard key={c.key} card={c} navigate={navigate} />)}
        </div>

        <p className="mt-8 text-center text-[12px] text-[#6b7280]">
          Not sure which fits?{" "}
          <button onClick={() => navigate(`${ROUTE}/signup`)} className="font-semibold text-[#007a9e] hover:underline" data-testid="signup-router-default">
            Start a single-school signup
          </button>{" "}
          — our team will help route you.
        </p>
        <p className="mt-3 text-center text-[12px] text-[#6b7280]">
          New here?{" "}
          <button onClick={() => navigate(`${ROUTE}/guide`)} className="font-semibold text-[#007a9e] hover:underline" data-testid="signup-router-guide">
            See how it works, step by step
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignupRouter;
